import { Component, h, Node, useMemo, useRef, useState } from "@lukekaalim/act"
import { TheatreDragSource, TheatreDragSubject, TheatreState } from "../../hooks"
import { html } from "@lukekaalim/act-web";
import * as three from 'three';
import { Animation, useAnimation, useLogger } from "@kosmod/kit";
import { renderBeat } from "@kosmod/graphics-3d";
import { createListAnimationHook } from "@kosmod/kit/list";

export type ListDropTargetProps<T> = {
  renderItem(item: T): Node,
  renderGap(): Node,
  calcKey(item: T): string,

  itemSize: three.Vector2Like,

  items: T[],

  acceptDrag(subject: TheatreDragSubject, source: TheatreDragSource): boolean,
  onDrop?(
    subject: TheatreDragSubject,
    source: TheatreDragSource,
    index: number,
    event: DragEvent
  ): void,
  findExistingIndex?(
    subject: TheatreDragSubject,
    source: TheatreDragSource
  ): null | number,
  
  state: TheatreState,
  className?: string,
}


export const ListDropTarget = <T>({
  state, renderItem, acceptDrag, calcKey,
  renderGap, items, itemSize, onDrop, findExistingIndex,

  className,
}: ListDropTargetProps<T>) => {
  const logger = useLogger('ListDropTarget');

  const useHorizontalListAnimation = useMemo(() => createListAnimationHook<({ type: 'item', item: T } | { type: 'gap' })>(
    render => render.type === 'gap' ? 'gap' : calcKey(render.item),
    200,
  ), [calcKey]);

  const accepted = state.selection.drag && acceptDrag(state.selection.drag.subject, state.selection.drag.source);
  const ref = useRef<HTMLOListElement | null>(null);

  const [dropIndex, setDropIndex] = useState<null | number>(null);
  
  const hideIndex = (findExistingIndex && state.selection.drag) ?
    findExistingIndex(state.selection.drag.subject, state.selection.drag.source) : null;

  const renderedItems: ({ type: 'item', item: T } | { type: 'gap' })[] = [];
  const hiddenItems = [];

  for (let i = 0; i < items.length; i++) {
    if (i === hideIndex)
      hiddenItems.push(items[i])
    else
      renderedItems.push({ type: 'item', item: items[i] })
  }

  if (dropIndex !== null && accepted) {
    renderedItems.splice(dropIndex, 0, { type: 'gap' })
  }
  if (dropIndex === null && accepted && hideIndex === null) {
    renderedItems.push({ type: 'gap' })
  }
  if (state.selection.drag && dropIndex === null && hideIndex !== null) {
    renderedItems.splice(hideIndex, 0, { type: 'gap' })
  }

  const animatedItems = useHorizontalListAnimation(renderedItems)

  return [
    h(html.ol, {
      ref,
      className,
      style: {
        listStyle: 'none',
        position: 'relative',
        // padding: '0',
        // margin: '0',
        height: itemSize.y + 'px',
        width: (renderedItems.length) * itemSize.x +  'px'
      },
      onDragOver(event) {
        const ol = ref.get()!;
        const rect = ol.getBoundingClientRect();

        const offsetX = event.clientX - rect.left;
        const index = Math.floor(offsetX / itemSize.x);
        
        setDropIndex(index)

        event.preventDefault();
      },
      onDrop(event) {
        if (onDrop && accepted && state.selection.drag && accepted !== null && dropIndex !== null) {
          onDrop(
            state.selection.drag.subject,
            state.selection.drag.source,
            dropIndex,
            event
          );
        }
        setDropIndex(null);
      },
      onDragLeave(event) {
        setDropIndex(null);
      },
    }, [
      ...animatedItems.map((animated, index) => {
        if (animated.state === 'exiting')
          return null;
        return h(AnimatedLI, {
          key: animated.key,
          animation: animated.position,
          state: animated.anim,
          itemSize,
        }, animated.element.type === 'gap' ? renderGap() : renderItem(animated.element.item))
      }),
      ...hiddenItems.map(item => {
        return h(AnimatedLI, {
          key: calcKey(item),
          animation: Animation(0, 0, [0, 0], () => 0),
          state: Animation(0, 0, [2, 2], () => 2),
          style: {
            position: 'absolute',
            display: 'none'
          },
          itemSize,
        }, renderItem(item))
      })
    ]),
  ];
}

type AnimatedLIProps = {
  state: Animation<number>,
  animation: Animation<number>,
  itemSize: three.Vector2Like,
}

const AnimatedLI: Component<AnimatedLIProps> = ({ children, animation, itemSize, state }) => {
  const ref = useRef<HTMLLIElement | null>(null);

  useAnimation(animation, left => {
    const li = ref.get()!; 
    li.style.transform = `translateX(${left * itemSize.x}px)`;
  })
  useAnimation(state, state => {
    const li = ref.get()!; 
    if (state > 1) {
      li.style.visibility = 'hidden';
      li.style.pointerEvents = 'none';
    } else {
      li.style.visibility = 'visible';
      li.style.pointerEvents = 'auto';
    }
  })

  return h(html.li, {
    ref,
    style: {
      position: 'absolute',
      left: '0px',
    },
  }, children)
}